import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import type { TFunction } from 'i18next';

import { PhoneField } from '@/components/PhoneField/PhoneField';
import { TextField } from '@/components/TextField';
import { Textarea } from '@/components/Textarea';

import './ExpressInterestModal.css';

// E.164 — same regex as SellPage / sell-submission-schema. Kept local because
// the modal is the only place phone is validated in the deal-page flow.
const E164_REGEX = /^\+[1-9]\d{1,14}$/;

export type InterestModalSubmitValues = {
  fullName: string;
  email: string;
  phone: string;
  message: string;
};

export type ExpressInterestModalProps = {
  open: boolean;
  dealId: string;
  dealTitle: string;
  referenceId: string;
  priceLabel: string;
  // ADR-025 §B — name + email are prefilled & read-only for authenticated
  // buyers (Spec §M4 prefill rule); the phone is prefilled but EDITABLE
  // (ADR-050 — it is persisted on the request as contact_phone). `null` renders
  // all inputs editable: that is the ANONYMOUS path (ADR-053) — a visitor with
  // no account fills name/email/phone, and DealPage submits via the public Edge.
  prefilledProfile: { fullName: string; email: string; phone: string } | null;
  onClose: () => void;
  onSubmit: (values: InterestModalSubmitValues) => Promise<void>;
};

function interestSchema(t: TFunction) {
  return z.object({
    fullName: z
      .string()
      .trim()
      .min(2, t('deal.modal.validation.fullNameRequired'))
      .max(140, t('deal.modal.validation.fullNameTooLong')),
    email: z
      .string()
      .trim()
      .toLowerCase()
      .min(1, t('deal.modal.validation.emailRequired'))
      .email(t('deal.modal.validation.emailInvalid'))
      .max(254, t('deal.modal.validation.emailInvalid')),
    phone: z
      .string()
      .trim()
      .min(1, t('deal.modal.validation.phoneRequired'))
      .regex(E164_REGEX, t('deal.modal.validation.phoneInvalidE164')),
    message: z.string().trim().max(500, t('deal.modal.validation.messageTooLong')),
  });
}

type FormValues = z.infer<ReturnType<typeof interestSchema>>;

export function ExpressInterestModal({
  open,
  dealTitle,
  referenceId,
  priceLabel,
  prefilledProfile,
  onClose,
  onSubmit,
}: ExpressInterestModalProps) {
  const { t } = useTranslation();
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
    control,
  } = useForm<FormValues>({
    resolver: zodResolver(interestSchema(t)),
    defaultValues: {
      fullName: prefilledProfile?.fullName ?? '',
      email: prefilledProfile?.email ?? '',
      phone: prefilledProfile?.phone ?? '',
      message: '',
    },
  });

  useEffect(() => {
    if (!open) return;
    closeButtonRef.current?.focus();
    setSubmitError(null);
    reset({
      fullName: prefilledProfile?.fullName ?? '',
      email: prefilledProfile?.email ?? '',
      phone: prefilledProfile?.phone ?? '',
      message: '',
    });
  }, [open, prefilledProfile, reset]);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !isSubmitting) onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, isSubmitting, onClose]);

  if (!open) return null;

  const readOnlyPrefill = prefilledProfile !== null;

  const submit = handleSubmit(async (values) => {
    setSubmitError(null);
    try {
      await onSubmit(values);
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : t('buyer.errors.generic'));
    }
  });

  return (
    <div
      className="confirm-modal__backdrop"
      role="presentation"
      onClick={() => {
        if (!isSubmitting) onClose();
      }}
    >
      <div
        className="confirm-modal conversion-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="interest-modal-title"
        onClick={(event) => event.stopPropagation()}
      >
        <h2 id="interest-modal-title" className="confirm-modal__title">
          {t('deal.modal.interestTitle')}
        </h2>
        <p className="conversion-modal__summary">
          {t('deal.modal.summary', {
            id: referenceId,
            title: dealTitle,
            price: priceLabel,
          })}
        </p>

        <form onSubmit={submit} className="conversion-modal__form" noValidate>
          <TextField
            label={t('deal.modal.fullName')}
            error={errors.fullName?.message}
            readOnly={readOnlyPrefill}
            {...register('fullName')}
          />
          <TextField
            label={t('deal.modal.email')}
            type="email"
            error={errors.email?.message}
            readOnly={readOnlyPrefill}
            {...register('email')}
          />
          <Controller
            control={control}
            name="phone"
            render={({ field, fieldState }) => (
              <PhoneField
                name="phone"
                label={t('deal.modal.phone')}
                placeholder="50 123 4567"
                required
                value={field.value ?? ''}
                onChange={field.onChange}
                error={fieldState.error?.message}
              />
            )}
          />
          <Textarea
            label={t('deal.modal.message')}
            placeholder={t('deal.modal.messagePlaceholder')}
            error={errors.message?.message}
            helper={t('deal.modal.messageHelper')}
            rows={4}
            {...register('message')}
          />

          {submitError ? (
            <p role="alert" className="conversion-modal__error">
              {submitError}
            </p>
          ) : null}

          <div className="confirm-modal__actions">
            <button
              ref={closeButtonRef}
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="confirm-modal__btn confirm-modal__btn--cancel"
            >
              {t('deal.modal.cancel')}
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="confirm-modal__btn confirm-modal__btn--confirm"
            >
              {isSubmitting ? t('deal.modal.sending') : t('deal.modal.send')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
